/**
 * Development Logger
 * Comprehensive logging system for development builds to track crashes and issues
 */

import { Alert } from 'react-native';

type LogPayload = unknown;
type ErrorWithOptionalStack = {
  message?: string;
  stack?: string;
};

export interface LogEntry {
  timestamp: string;
  level: 'DEBUG' | 'INFO' | 'WARN' | 'ERROR' | 'CRASH';
  category: string;
  message: string;
  data?: string;
  stack?: string;
}

class DevLogger {
  private logs: LogEntry[] = [];
  private maxLogs = 500; // Keep last 500 logs
  private isDev = __DEV__ || process.env.NODE_ENV === 'development';

  private getErrorStack(error: unknown): string | undefined {
    if (typeof error === 'object' && error !== null && 'stack' in error) {
      return (error as ErrorWithOptionalStack).stack;
    }

    return undefined;
  }

  private addLog(level: LogEntry['level'], category: string, message: string, data?: LogPayload, stack?: string) {
    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      category,
      message,
      data: data ? JSON.stringify(data, null, 2) : undefined,
      stack
    };

    this.logs.push(entry);
    
    // Keep only the last maxLogs entries
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(-this.maxLogs);
    }

    // Always log to console in development
    if (this.isDev) {
      const logMethod = level === 'ERROR' || level === 'CRASH' ? console.error : 
                       level === 'WARN' ? console.warn : console.log;
      
      logMethod(`[${level}] [${category}] ${message}`, data || '');
      if (stack) {
        console.error('Stack:', stack);
      }
    }
  }

  private normalizeArgs(
    defaultCategory: string,
    categoryOrMessage: string,
    messageOrData?: LogPayload,
    data?: LogPayload
  ) {
    if (typeof messageOrData === 'string') {
      return {
        category: categoryOrMessage,
        message: messageOrData,
        data
      };
    }

    return {
      category: defaultCategory,
      message: categoryOrMessage,
      data: messageOrData
    };
  }

  debug(category: string, message: string, data?: LogPayload): void;
  debug(message: string, data?: LogPayload): void;
  debug(categoryOrMessage: string, messageOrData?: LogPayload, data?: LogPayload) {
    const normalized = this.normalizeArgs('APP', categoryOrMessage, messageOrData, data);
    this.addLog('DEBUG', normalized.category, normalized.message, normalized.data);
  }

  info(category: string, message: string, data?: LogPayload): void;
  info(message: string, data?: LogPayload): void;
  info(categoryOrMessage: string, messageOrData?: LogPayload, data?: LogPayload) {
    const normalized = this.normalizeArgs('APP', categoryOrMessage, messageOrData, data);
    this.addLog('INFO', normalized.category, normalized.message, normalized.data);
  }

  warn(category: string, message: string, data?: LogPayload): void;
  warn(message: string, data?: LogPayload): void;
  warn(categoryOrMessage: string, messageOrData?: LogPayload, data?: LogPayload) {
    const normalized = this.normalizeArgs('APP', categoryOrMessage, messageOrData, data);
    this.addLog('WARN', normalized.category, normalized.message, normalized.data);
  }

  error(category: string, message: string, error?: LogPayload): void;
  error(message: string, error?: LogPayload): void;
  error(categoryOrMessage: string, messageOrError?: LogPayload, error?: LogPayload) {
    const normalized = this.normalizeArgs('APP', categoryOrMessage, messageOrError, error);
    const stack = this.getErrorStack(normalized.data) || new Error().stack;
    this.addLog('ERROR', normalized.category, normalized.message, normalized.data, stack);
  }

  crash(category: string, message: string, error?: LogPayload): void;
  crash(message: string, error?: LogPayload): void;
  crash(categoryOrMessage: string, messageOrError?: LogPayload, error?: LogPayload) {
    const normalized = this.normalizeArgs('APP', categoryOrMessage, messageOrError, error);
    const stack = this.getErrorStack(normalized.data) || new Error().stack;
    this.addLog('CRASH', normalized.category, normalized.message, normalized.data, stack);
    
    // Show crash details in development
    if (this.isDev) {
      setTimeout(() => {
        Alert.alert(
          'Development Crash Detected',
          `Category: ${normalized.category}\nMessage: ${normalized.message}\n\nCheck console for full details.`,
          [
            { text: 'Show Logs', onPress: () => this.showRecentLogs() },
            { text: 'OK' }
          ]
        );
      }, 100);
    }
  }

  log(message: string, data?: LogPayload) {
    this.info('APP', message, data);
  }

  // Get recent logs for debugging
  getRecentLogs(count: number = 50): LogEntry[] {
    return this.logs.slice(-count);
  }

  // Show recent logs in an alert (for development)
  showRecentLogs() {
    const recentLogs = this.getRecentLogs(10);
    const logText = recentLogs.map(log => 
      `[${log.level}] ${log.category}: ${log.message}`
    ).join('\n');
    
    Alert.alert('Recent Logs', logText);
  }

  // Get all logs as formatted string for sharing/debugging
  exportLogs(): string {
    return this.logs.map(log => {
      let entry = `[${log.timestamp}] [${log.level}] [${log.category}] ${log.message}`;
      if (log.data) {
        entry += `\nData: ${log.data}`;
      }
      if (log.stack) {
        entry += `\nStack: ${log.stack}`;
      }
      return entry;
    }).join('\n\n');
  }

  // Clear all logs
  clear() {
    this.logs = [];
  }

  // Track Live Event specific operations
  liveEvent = {
    createStart: (params: LogPayload) => this.info('LIVE_EVENT', 'Event creation started', params),
    createSuccess: (eventId: string) => this.info('LIVE_EVENT', 'Event created successfully', { eventId }),
    createError: (error: LogPayload) => this.error('LIVE_EVENT', 'Event creation failed', error),
    
    loadStart: (eventId: string) => this.info('LIVE_EVENT', 'Loading event data', { eventId }),
    loadSuccess: (eventId: string) => this.info('LIVE_EVENT', 'Event data loaded successfully', { eventId }),
    loadError: (eventId: string, error: LogPayload) => this.error('LIVE_EVENT', 'Event data load failed', { eventId, error }),
    
    subscriptionStart: (eventId: string, type: string) => this.debug('LIVE_EVENT', `Starting ${type} subscription`, { eventId }),
    subscriptionSuccess: (eventId: string, type: string) => this.debug('LIVE_EVENT', `${type} subscription successful`, { eventId }),
    subscriptionError: (eventId: string, type: string, error: LogPayload) => this.error('LIVE_EVENT', `${type} subscription failed`, { eventId, error }),
    
    navigationStart: (from: string, to: string) => this.info('LIVE_EVENT', 'Navigation started', { from, to }),
    navigationSuccess: (from: string, to: string) => this.info('LIVE_EVENT', 'Navigation completed', { from, to }),
    navigationError: (from: string, to: string, error: LogPayload) => this.error('LIVE_EVENT', 'Navigation failed', { from, to, error }),
    
    crash: (location: string, error: LogPayload) => this.crash('LIVE_EVENT', `Crash in ${location}`, error)
  };
}

// Global logger instance
export const devLogger = new DevLogger();

// Global error handler for unhandled errors (React Native only)
if (__DEV__ && typeof ErrorUtils !== 'undefined') {
  const originalHandler = ErrorUtils.getGlobalHandler();
  ErrorUtils.setGlobalHandler((error, isFatal) => {
    devLogger.crash('GLOBAL', 'Unhandled error detected', { error: error.message, isFatal, stack: error.stack });
    originalHandler(error, isFatal);
  });
}

export default devLogger;
